import { useEffect, useState } from "react";
import { Heart, RefreshCw, BookOpen, Quote, AtSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { QuoteMakers, useTracks } from "@/components/QuoteTools";

const TOPIC_LABEL = (t) => t.charAt(0).toUpperCase() + t.slice(1);

const HandleSetting = () => {
  const [handle, setHandle] = useState("");
  const [saved, setSaved] = useState("");
  useEffect(() => { api.get("/mindset/handle").then((r) => { setHandle(r.data.handle); setSaved(r.data.handle); }).catch(() => {}); }, []);
  const save = async () => {
    try {
      const r = await api.put("/mindset/handle", { handle });
      setHandle(r.data.handle); setSaved(r.data.handle);
      toast.success(r.data.handle ? `${r.data.handle} will show on your cards and videos.` : "Handle removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your handle."); }
  };
  return (
    <div className="card-panel p-4" data-testid="handle-setting">
      <p className="text-sm text-white font-bold flex items-center gap-1.5"><AtSign size={14} className="text-[#FD0F10]" /> Your handle on cards and videos</p>
      <p className="text-xs text-gray-400 mt-1">Shown small under the author's name. ihatemyjob.app always stays on. Leave it empty to switch it off.</p>
      <div className="mt-3 flex gap-2">
        <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@yourhandle" maxLength={31} className="flex-1 bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm text-gray-100" data-testid="handle-input" />
        <button onClick={save} disabled={handle === saved} className="hero-accent text-white text-xs font-mono-ui uppercase tracking-wider px-4 rounded disabled:opacity-50" data-testid="handle-save-btn">Save</button>
      </div>
    </div>
  );
};

const QuoteItem = ({ q, fav, onFav, isPro, big, tracks }) => (
  <div className={`card-panel ${big ? "p-6" : "p-4"}`} data-testid={`quote-${q.id}`}>
    <p className={`${big ? "text-xl sm:text-2xl font-display font-bold" : "text-base"} text-white leading-snug`}>"{q.text}"</p>
    <p className="mt-2 text-sm text-[#FD0F10] font-bold">{q.author}</p>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button onClick={() => onFav(q.id)} className={`flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-3 py-1.5 rounded border ${fav ? "border-[#FD0F10] text-[#FD0F10]" : "border-[#2A2E39] text-gray-300"}`} data-testid={`quote-fav-${q.id}`}>
        <Heart size={13} fill={fav ? "#FD0F10" : "none"} /> {fav ? "Saved" : "Save"}
      </button>
      <QuoteMakers q={q} isPro={isPro} tracks={tracks} />
    </div>
  </div>
);

const Quotes = ({ isPro }) => {
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [topic, setTopic] = useState("");
  const [author, setAuthor] = useState("");
  const [favsOnly, setFavsOnly] = useState(false);
  const [shown, setShown] = useState(30);
  const tracks = useTracks(isPro);
  // The featured quote follows the chosen topic/author/favourites and changes to a fresh one each time.
  const nextQuote = () => api.get("/mindset/quotes/next", { params: { topic: topic || undefined, author: author || undefined, favs: favsOnly, skip: today?.id } })
    .then((r) => setToday(r.data.quote)).catch(() => setToday(null));
  useEffect(() => {
    nextQuote();
    api.get("/mindset/quotes", { params: { topic: topic || undefined, author: author || undefined, favs: favsOnly } }).then((r) => { setData(r.data); setShown(30); }).catch(() => {});
  }, [topic, author, favsOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  const onFav = async (id) => {
    const r = await api.post(`/mindset/quotes/${id}/fav`);
    setData((d) => ({ ...d, favs: r.data.fav ? [...d.favs, id] : d.favs.filter((x) => x !== id), quotes: favsOnly && !r.data.fav ? d.quotes.filter((q) => q.id !== id) : d.quotes }));
  };
  const favs = data?.favs || [];
  const sel = "bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm text-gray-100";
  return (
    <div className="space-y-4">
      {isPro && <HandleSetting />}
      {today && (
        <div>
          <QuoteItem key={today.id} q={today} fav={favs.includes(today.id)} onFav={onFav} isPro={isPro} tracks={tracks} big />
          <button onClick={nextQuote} className="mt-2 flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-gray-300 hover:text-white" data-testid="quote-next-btn"><RefreshCw size={13} /> Next quote</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-2" data-testid="quote-filters">
        <select value={topic} onChange={(e) => setTopic(e.target.value)} className={sel} data-testid="quote-topic-select">
          <option value="">All topics</option>
          {(data?.topics || []).map((t) => <option key={t} value={t}>{TOPIC_LABEL(t)}</option>)}
        </select>
        <select value={author} onChange={(e) => setAuthor(e.target.value)} className={sel} data-testid="quote-author-select">
          <option value="">All authors</option>
          {(data?.authors || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button onClick={() => setFavsOnly(!favsOnly)} className={`${sel} ${favsOnly ? "border-[#FD0F10] text-[#FD0F10]" : ""}`} data-testid="quote-favs-toggle">My favourites</button>
      </div>
      {data && !data.quotes.length && <p className="text-sm text-gray-400" data-testid="quote-empty">{favsOnly ? "No favourites yet. Tap Save on any quote." : "No quotes here yet."}</p>}
      <div className="space-y-3">
        {(data?.quotes || []).slice(0, shown).map((q) => <QuoteItem key={q.id} q={q} fav={favs.includes(q.id)} onFav={onFav} isPro={isPro} tracks={tracks} />)}
      </div>
      {data && data.quotes.length > shown && (
        <button onClick={() => setShown(shown + 30)} className="w-full border border-[#2A2E39] rounded py-2 text-sm text-gray-300 hover:text-white" data-testid="quote-load-more">Show more quotes</button>
      )}
    </div>
  );
};

const Books = () => {
  const [books, setBooks] = useState(null);
  useEffect(() => { api.get("/mindset/books").then((r) => setBooks(r.data.books)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="books-list">
      {(books || []).map((b) => (
        <div key={b.id} className="card-panel p-4" data-testid={`book-${b.id}`}>
          <p className="font-display font-bold text-white">{b.title}</p>
          <p className="text-sm text-[#FD0F10]">{b.author}</p>
          <p className="mt-1 text-sm text-gray-300">{b.about}</p>
        </div>
      ))}
    </div>
  );
};

export default function Mindset() {
  const { user } = useAuth();
  const [tab, setTab] = useState("quotes");
  const tabCls = (t) => `flex-1 flex items-center justify-center gap-2 py-2.5 rounded font-mono-ui text-xs uppercase tracking-wider ${tab === t ? "bg-[#FD0F10] text-white" : "text-gray-300 border border-[#2A2E39]"}`;
  return (
    <div className="max-w-2xl mx-auto px-5 pt-6 pb-28" data-testid="mindset-page">
      <h1 className="font-display font-black text-3xl sm:text-4xl text-white">Mindset</h1>
      <p className="mt-1 text-sm text-gray-400">Real quotes and books to keep your head right while you build your way out.</p>
      <div className="mt-5 flex gap-2" data-testid="mindset-tabs">
        <button onClick={() => setTab("quotes")} className={tabCls("quotes")} data-testid="mindset-tab-quotes"><Quote size={14} /> Quotes</button>
        <button onClick={() => setTab("books")} className={tabCls("books")} data-testid="mindset-tab-books"><BookOpen size={14} /> Books</button>
      </div>
      <div className="mt-5">{tab === "quotes" ? <Quotes isPro={!!user?.is_premium} /> : <Books />}</div>
    </div>
  );
}
